/**
 * AI Assistant streaming service.
 *
 * The /ai-assistant/chat endpoint emits Server-Sent Events (SSE). It still goes
 * through the shared `api` instance, so AuthProvider's interceptors handle it like
 * every other request: the token, the 24-hour password dialog on SESSION_EXPIRED,
 * the sign-out on a permanent 401 and the one-time token refresh. Only the
 * transport differs (see `streamChat`).
 */

import api from '../utils/api';

// ---------------------------------------------------------------------------
// AgentEvent — mirrors backend/src/modules/ai-assistant/agent/agent.types.ts
// ---------------------------------------------------------------------------

export type AgentEvent =
  | { type: 'token'; text: string }
  | { type: 'tool_call'; name: string }
  | { type: 'tool_result'; name: string }
  | { type: 'data'; columns: string[]; rows: unknown[][] }
  // assistantMessageIds is additive — present on newer backends, used to attach
  // thumbs feedback to the answer that was just streamed.
  | { type: 'done'; conversationId: string; assistantMessageIds?: string[] }
  | { type: 'error'; message: string }
  | { type: 'confirm_required'; actionId: string; toolName: string; title: string; summary: string; fields: { label: string; value: string }[] };

// ---------------------------------------------------------------------------
// Typed error for pre-stream HTTP failures (a refusal before the stream opens)
// ---------------------------------------------------------------------------

/** Shaped like an axios error: the `api` interceptors read a refusal's code off `response`. */
export interface StreamHttpError extends Error {
  status: number;
  response: { status: number; data: unknown };
}

// ---------------------------------------------------------------------------
// streamChat
// ---------------------------------------------------------------------------

export interface StreamChatParams {
  message: string;
  conversationId?: string;
  /** IANA timezone for clinic-local time formatting. Defaults to the browser's
   *  resolved timezone so the assistant's times match the calendar. */
  timeZone?: string;
}

export interface StreamChatHandlers {
  onEvent: (e: AgentEvent) => void;
}

/**
 * Opens a streaming connection to POST /ai-assistant/chat and pipes
 * AgentEvent objects to `handlers.onEvent` as they arrive.
 *
 * Throws a `StreamHttpError` (with `.status`) on a refusal before the stream
 * opens (401 SESSION_EXPIRED, 403 SUBSCRIPTION_REQUIRED, etc.), once the api
 * interceptors have handled it.
 *
 * Resolves when the stream ends naturally or an AbortSignal fires.
 */
export async function streamChat(
  params: StreamChatParams,
  handlers: StreamChatHandlers,
  opts?: { signal?: AbortSignal },
): Promise<void> {
  // Send the browser's timezone so the backend formats times to match the
  // calendar (which renders in browser-local time). Caller can override.
  const timeZone =
    params.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { data: stream } = await api.post<ReadableStream<Uint8Array>>(
    '/ai-assistant/chat',
    { ...params, timeZone },
    {
      signal: opts?.signal,
      // fetch hands the body over as it arrives (axios's default XHR holds it to the
      // end); axios's own 'fetch' adapter would leave a refusal's body unread, hiding
      // its code from the interceptors; and a custom adapter sets no 30 s timeout.
      adapter: async (config) => {
        const res = await fetch(api.getUri(config), {
          method: 'POST',
          headers: config.headers.toJSON(true),
          body: config.data,
          signal: config.signal as AbortSignal | undefined,
        });
        const response = { status: res.status, statusText: res.statusText, headers: {}, config };
        if (res.ok) return { ...response, data: res.body };
        // A refusal arrives as JSON before the stream opens; once the headers have
        // flushed, errors come as SSE `error` events instead.
        const data = await res.json().catch(() => ({}));
        throw Object.assign(new Error(String(data?.message ?? `HTTP ${res.status}`)), {
          status: res.status,
          response: { ...response, data },
          config,
        });
      },
    },
  );

  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buf = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buf += decoder.decode(value, { stream: true });

    // SSE frames are separated by double newline. Split on '\n\n' and keep
    // any trailing partial frame in `buf` for the next read.
    const parts = buf.split('\n\n');
    buf = parts.pop() ?? '';

    for (const part of parts) {
      // Strip the leading 'data: ' prefix defined by the SSE spec.
      const line = part.replace(/^data:\s*/, '').trim();
      if (!line) continue;
      try {
        const event = JSON.parse(line) as AgentEvent;
        handlers.onEvent(event);
      } catch {
        // Malformed frame — skip silently.
      }
    }
  }

  // Flush any final partial frame (edge case: stream closed without trailing \n\n).
  const remaining = buf.replace(/^data:\s*/, '').trim();
  if (remaining) {
    try {
      const event = JSON.parse(remaining) as AgentEvent;
      handlers.onEvent(event);
    } catch {
      // ignore
    }
  }
}

// ---------------------------------------------------------------------------
// Confirm / cancel pending write actions (plain JSON — not SSE)
// ---------------------------------------------------------------------------

export interface ConfirmActionResult { message: string; targetType: string; targetId: string; }

export async function confirmAction(actionId: string): Promise<ConfirmActionResult> {
  const { data } = await api.post(`/ai-assistant/actions/${actionId}/confirm`);
  return data;
}

export async function cancelAction(actionId: string): Promise<{ cancelled: boolean }> {
  const { data } = await api.post(`/ai-assistant/actions/${actionId}/cancel`);
  return data;
}

// ---------------------------------------------------------------------------
// Answer feedback
// ---------------------------------------------------------------------------

/** Rate an assistant answer: 1 = helpful, -1 = not. Costs no AI quota. */
export async function rateMessage(
  messageId: string,
  rating: 1 | -1,
): Promise<{ rated: boolean }> {
  const { data } = await api.post(`/ai-assistant/messages/${messageId}/feedback`, { rating });
  return data;
}
